// src/config/errorConfig.js

/**
 * Centralized error configuration
 * Usage:
 *   import { getErrorMessage } from './errorConfig';
 *   const message = getErrorMessage('AUTH_INVALID_CREDENTIALS');
 */

// ======================
// ERROR DEFINITIONS
// ======================

const ERRORS = {
  // === AUTHENTICATION ERRORS ===
  AUTH_INVALID_CREDENTIALS: {
    code: 'AUTH_INVALID_CREDENTIALS',
    message: 'Invalid username or password. Please try again.',
    detail: 'Login attempt failed due to incorrect credentials.',
    category: 'auth',
    httpStatus: 401,
  },
  AUTH_USER_NOT_FOUND: {
    code: 'AUTH_USER_NOT_FOUND',
    message: 'No account found with this username.',
    detail: 'User does not exist in the system.',
    category: 'auth',
    httpStatus: 404,
  },
  AUTH_MISSING_FIELDS: {
    code: 'AUTH_MISSING_FIELDS',
    message: 'Username and password are required.',
    detail: 'Login request missing required fields.',
    category: 'auth',
    httpStatus: 400,
  },
  AUTH_SESSION_EXPIRED: {
    code: 'AUTH_SESSION_EXPIRED',
    message: 'Your session has expired. Please sign in again.',
    detail: 'JWT token is expired or invalid.',
    category: 'auth',
    httpStatus: 401,
  },
  AUTH_SESSION_TAKEN_OVER: {
    code: 'AUTH_SESSION_TAKEN_OVER',
    message: 'Your account was signed in on another device, so Orbiee disconnected here. Open the chat and tap "Continue here" to pick up on this device.',
    detail: 'The agent WebSocket received { type: "system", status: "session_takeover" }: the backend moved the session to a newer connection. No socket opens until the shopper resumes (services/websocket/sessionTakeover.js).',
    category: 'auth',
    httpStatus: null,
  },
  AUTH_ROLE_INVALID: {
    code: 'AUTH_ROLE_INVALID',
    message: 'Invalid user role detected. Access denied.',
    detail: 'User role is neither "A" (admin) nor "C" (customer).',
    category: 'auth',
    httpStatus: 403,
  },

  // === NETWORK / CONNECTION ERRORS ===
  NETWORK_OFFLINE: {
    code: 'NETWORK_OFFLINE',
    message: 'You are offline. Please check your internet connection.',
    detail: 'Browser reports navigator.onLine === false.',
    category: 'network',
    httpStatus: null,
  },
  NETWORK_TIMEOUT: {
    code: 'NETWORK_TIMEOUT',
    message: 'Request timed out. Please try again.',
    detail: 'Server did not respond within expected time.',
    category: 'network',
    httpStatus: null,
  },
  NETWORK_SERVER_UNREACHABLE: {
    code: 'NETWORK_SERVER_UNREACHABLE',
    message: 'Unable to connect to server. Please try again later.',
    detail: 'Failed to fetch due to DNS, CORS, or server down.',
    category: 'network',
    httpStatus: null,
  },

  // === API / SERVER ERRORS ===
  API_SERVER_ERROR: {
    code: 'API_SERVER_ERROR',
    message: 'Something went wrong on our end. Please try again.',
    detail: 'Internal server error (5xx) from backend.',
    category: 'api',
    httpStatus: 500,
  },
  API_BAD_REQUEST: {
    code: 'API_BAD_REQUEST',
    message: 'Invalid request. Please check your input.',
    detail: 'Server rejected request due to malformed data.',
    category: 'api',
    httpStatus: 400,
  },
  API_NOT_FOUND: {
    code: 'API_NOT_FOUND',
    message: 'Requested resource not found.',
    detail: '404 Not Found from API endpoint.',
    category: 'api',
    httpStatus: 404,
  },
  API_RATE_LIMITED: {
    code: 'API_RATE_LIMITED',
    message: 'Too many requests. Please wait and try again.',
    detail: 'Rate limit exceeded (429).',
    category: 'api',
    httpStatus: 429,
  },

  // === VALIDATION ERRORS ===
  VALIDATION_USERNAME_REQUIRED: {
    code: 'VALIDATION_USERNAME_REQUIRED',
    message: 'Username is required.',
    detail: 'Username field is empty.',
    category: 'validation',
    httpStatus: 400,
  },
  VALIDATION_PASSWORD_REQUIRED: {
    code: 'VALIDATION_PASSWORD_REQUIRED',
    message: 'Password is required.',
    detail: 'Password field is empty.',
    category: 'validation',
    httpStatus: 400,
  },
  VALIDATION_PASSWORD_TOO_SHORT: {
    code: 'VALIDATION_PASSWORD_TOO_SHORT',
    message: 'Password must be at least 6 characters long.',
    detail: 'Password length < 6.',
    category: 'validation',
    httpStatus: 400,
  },

  // === DATABASE ERRORS ===
  DB_CONNECTION_FAILED: {
    code: 'DB_CONNECTION_FAILED',
    message: 'Database is temporarily unavailable. Please try again.',
    detail: 'SQLAlchemy or MySQL connection error.',
    category: 'database',
    httpStatus: 500,
  },
  DB_QUERY_FAILED: {
    code: 'DB_QUERY_FAILED',
    message: 'Failed to retrieve data. Please try again.',
    detail: 'SQL query execution failed.',
    category: 'database',
    httpStatus: 500,
  },

  // === GENERAL / UI ERRORS ===
  GENERAL_UNKNOWN_ERROR: {
    code: 'GENERAL_UNKNOWN_ERROR',
    message: 'An unexpected error occurred. Please try again.',
    detail: 'Unmapped error – check logs for details.',
    category: 'general',
    httpStatus: 500,
  },
  GENERAL_FEATURE_NOT_SUPPORTED: {
    code: 'GENERAL_FEATURE_NOT_SUPPORTED',
    message: 'This feature is not available yet.',
    detail: 'Functionality is disabled or not implemented.',
    category: 'general',
    httpStatus: 501,
  },
  // === VALIDATION ERRORS ===
VALIDATION_CUSTOMER_ID_REQUIRED: {
  code: 'VALIDATION_CUSTOMER_ID_REQUIRED',
  message: 'Customer ID is required to load preferences.',
  detail: 'Attempted to fetch user preferences without a valid customer_id.',
  category: 'validation',
  httpStatus: 400,
},// === VALIDATION ERRORS ===
VALIDATION_CATEGORY_REQUIRED: {
  code: 'VALIDATION_CATEGORY_REQUIRED',
  message: 'Category and subcategory are required to fetch products.',
  detail: 'Product fetch attempted with missing category or subcategory.',
  category: 'validation',
  httpStatus: 400,
},

// === API ERRORS ===
API_INVALID_JSON: {
  code: 'API_INVALID_JSON',
  message: 'Received invalid data from server. Please try again later.',
  detail: 'Server response was not valid JSON.',
  category: 'api',
  httpStatus: 502,
},// === VALIDATION ERRORS ===
VALIDATION_SEARCH_QUERY_REQUIRED: {
  code: 'VALIDATION_SEARCH_QUERY_REQUIRED',
  message: 'Search query cannot be empty.',
  detail: 'Product search attempted with empty or missing query.',
  category: 'validation',
  httpStatus: 400,
},

// === API ERRORS ===
API_INVALID_RESPONSE_FORMAT: {
  code: 'API_INVALID_RESPONSE_FORMAT',
  message: 'Received unexpected data format from server.',
  detail: 'API response missing expected "products" array or structure.',
  category: 'api',
  httpStatus: 502,
},
API_PRODUCT_NOT_FOUND: {
  code: 'API_PRODUCT_NOT_FOUND',
  message: 'Product not found.',
  detail: 'Requested product ID does not exist.',
  category: 'api',
  httpStatus: 404,
}
};

// ======================
// HELPER FUNCTIONS
// ======================

/**
 * Get user-friendly error message by code
 * @param {string} errorCode - e.g., 'AUTH_INVALID_CREDENTIALS'
 * @returns {string} User-facing message
 */
export const getErrorMessage = (errorCode) => {
  return ERRORS[errorCode]?.message || ERRORS.GENERAL_UNKNOWN_ERROR.message;
};

/**
 * Get full error object
 * @param {string} errorCode
 * @returns {object} Full error config
 */
export const getErrorConfig = (errorCode) => {
  return ERRORS[errorCode] || ERRORS.GENERAL_UNKNOWN_ERROR;
};

/**
 * Map HTTP status to error code (for generic API errors)
 * @param {number} status
 * @returns {string} errorCode
 */
export const getErrorCodeByHttpStatus = (status) => {
  if (!status) return 'NETWORK_SERVER_UNREACHABLE';
  if (status >= 500) return 'API_SERVER_ERROR';
  if (status === 401) return 'AUTH_INVALID_CREDENTIALS';
  if (status === 403) return 'AUTH_ROLE_INVALID';
  if (status === 404) return 'API_NOT_FOUND';
  if (status === 429) return 'API_RATE_LIMITED';
  if (status >= 400) return 'API_BAD_REQUEST';
  return 'GENERAL_UNKNOWN_ERROR';
};

/**
 * Check if error is network-related
 * @param {string} errorCode
 * @returns {boolean}
 */
export const isNetworkError = (errorCode) => {
  const config = getErrorConfig(errorCode);
  return config.category === 'network';
};

// ======================
// EXPORTS
// ======================

export default ERRORS;